<template>
  <v-menu
    v-model="calendarOpen"
    offset-y
    :close-on-content-click="false"
    min-width="280"
    content-class="dateRangePicker"
  >
    <template v-slot:activator="{ on }">
      <div
        v-ripple
        class="dateRangePicker_dates"
        :class="{
          'dateRangePicker_dates-open': calendarOpen,
          'dateRangePicker_dates-active': active,
        }"
        v-on="on"
      >
        <div class="dateRangePicker_select">
          <span class="dateRangePicker_icon icon icon-calendar" />
          <span class="dateRangePicker_date">{{
            dates[0] | date('YYYY/M/D', 'YYYYMMDDHHmmss')
          }}</span>
          <span class="dateRangePicker_time">{{
            dates[0] | date('HH:mm:00', 'YYYYMMDDHHmmss')
          }}</span>
        </div>
        <span class="dateRangePicker_separator">~</span>
        <div class="dateRangePicker_select">
          <span class="dateRangePicker_icon icon icon-calendar" />
          <span class="dateRangePicker_date">{{
            dates[1] | date('YYYY/M/D', 'YYYYMMDDHHmmss')
          }}</span>
          <span class="dateRangePicker_time">{{
            dates[1] | date('HH:mm:00', 'YYYYMMDDHHmmss')
          }}</span>
        </div>
      </div>
    </template>
    <date-picker
      ref="datePicker"
      :key="calendarKey"
      v-model="range"
      locale="ja"
      mode="dateTime"
      :masks="masks"
      :max-date="maxDate"
      :min-date="minDate"
      :min-range="customRange"
      :max-range-day="maxRangeDay"
      is24hr
      is-range
      is-expanded
      @input="
        $emit('onInput', $event);
        changeRange();
      "
    />
    <div class="dateRangePicker_actions">
      <div class="dateRangePicker_action">
        <v-btn
          text
          class="dateRangePicker_btn dateRangePicker_btn-cancel"
          @click="cancel"
        >
          キャンセル
        </v-btn>
      </div>
      <div class="dateRangePicker_action">
        <v-btn
          text
          class="dateRangePicker_btn dateRangePicker_btn-ok"
          @click="submit"
        >
          設定
        </v-btn>
      </div>
    </div>
  </v-menu>
</template>

<script>
import dayjs from 'dayjs';
import DatePicker from 'v-calendar/src/components/DatePicker.vue';
import { getDefaultRangeCalendar, subtractDays } from '@/utils/date';

export default {
  name: 'DateRangePicker',
  components: { DatePicker },
  props: {
    dates: {
      type: Array,
      default: () => getDefaultRangeCalendar(),
    },
    active: {
      type: Boolean,
      default: false,
    },
    minDate: {
      type: Date,
      default: () => null,
    },
    maxDate: {
      type: Date,
      default: () => new Date(),
    },
    minRange: {
      type: Number,
      default: null,
    },
    maxRangeDay: {
      type: Number,
      default: null,
    },
    flexibleRange: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const range = getDefaultRangeCalendar();
    return {
      masks: {
        data: 'YYYY/M/DD hh:mm:00',
        inputDateTime: 'YYYY/M/DD hh:mm:00',
        iso: 'YYYY/M/DD hh:mm:00',
        input: ['YYYY-MM-DD', 'YYYY/MM/DD'],
        inputDateTime24hr: ['YYYY-MM-DD HH:mm', 'YYYY/MM/DD HH:mm'],
        navMonths: 'MM',
        title: 'YYYY/M',
      },
      range: { start: range[0], end: range[1] },
      calendarOpen: false,
      isTimeChanged: false,
      customRange: 10,
      originalDates: [subtractDays(new Date(), 1), new Date()],
      calendarKey: Date.now(),
    };
  },
  watch: {
    calendarOpen(status) {
      if (status) {
        this.calendarKey = Date.now();
      }
      if (!status && !this.isTimeChanged) {
        // Close/Cancel to revert
        this.$set(this.range, 'start', this.originalDates[0]);
        this.$set(this.range, 'end', this.originalDates[1]);
      } else {
        // Reset status when opening calendar
        this.isTimeChanged = false;
      }
    },
    minDate: {
      handler() {
        this.originalDates = this.dates;
      },
    },
    dates: {
      deep: true,
      handler(val) {
        this.$set(this.range, 'start', val[0]);
        if (this.flexibleRange) {
          const period = dayjs(val[1]).diff(dayjs(val[0]));
          this.$nextTick(() => {
            if (period <= 600000) {
              this.customRange = this.minRange;
              const newMinute = dayjs(val[0]).add(this.minRange, 'minute');
              this.$set(this.range, 'end', newMinute.format('YYYYMMDDHHmm00'));
            } else {
              this.customRange = null;
              this.$set(this.range, 'end', val[1]);
            }
          });
        } else {
          this.$set(this.range, 'end', val[1]);
        }
      },
    },
  },
  created() {
    this.range = {
      start: this.dates[0],
      end: this.dates[1],
    };
    this.customRange = this.minRange;
    this.originalDates = this.dates;
  },
  methods: {
    submit() {
      this.isTimeChanged = true;
      this.calendarOpen = false;
      this.changeRange(true);
      this.$emit('submit');
    },
    changeRange(onSubmit = false) {
      let startDate = dayjs(this.range.start);
      let endDate = dayjs(this.range.end);
      const dates = [
        startDate.format('YYYYMMDDHHmm') + '00',
        endDate.format('YYYYMMDDHHmm') + '00',
      ];
      this.$emit('update:dates', dates);
      if (onSubmit) {
        // If submit (設定), set dates
        this.originalDates = dates;
        this.$emit('applySetDates', dates);
      }
    },
    cancel() {
      this.calendarOpen = false;
      this.$emit('cancel');
    },
  },
};
</script>

<style lang="scss">
.dateRangePicker {
  background-color: #fff;
  &_dates {
    display: flex;
    width: 187px;
    color: #666;
    border-radius: 5px;
    flex-wrap: wrap;
    margin-top: 10px;
    &-open {
      background-color: #eeeeee;
    }
    &-active {
      color: var(--v-primary-base);
    }
  }
  &_select {
    display: flex;
    align-items: center;
    height: 20px;
    margin: 3px;
    cursor: pointer;
  }
  &_date {
    display: flex;
    width: 80px;
    font-size: 14px;
    font-weight: 400;
    padding-left: 6px;
  }
  &_time {
    display: flex;
    width: 70px;
    font-size: 14px;
    font-weight: 400;
    padding-left: 5px;
  }
  &_separator {
    display: flex;
    height: 20px;
    line-height: 20px;
    margin-top: 3px;
  }
  .vc-container {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }
  .vc-container.vc-is-expanded {
    border-bottom: none;
    min-width: calc(100% + 1px);
  }
  .vc-header.align-center {
    padding: 1px 0;
    border-bottom: solid 1px rgba(0, 0, 0, 0.06);
  }
  .vc-arrows-container.title-center {
    top: -9px;
  }
  .vc-time-picker.vc-bordered {
    border-top: none;
  }
  &_actions {
    display: flex;
    width: 100%;
    height: 50px;
    align-items: center;
  }
  &_action {
    display: flex;
    justify-content: center;
    width: 50%;
  }
  &_btn {
    .v-btn__content {
      font-size: 14px;
      font-weight: bold;
    }
    &-cancel {
      .v-btn__content {
        font-size: 14px;
        font-weight: normal;
        font-stretch: normal;
        font-style: normal;
        letter-spacing: normal;
        text-align: center;
        color: #0075c2;
      }
    }
  }
}
</style>
